import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Button, Divider, Typography } from '@/primer/components';
import { PageLayout } from '@/components/layout';
import { AccountLockout, SocialAuthButton } from '@/components/ui';
import type { SocialAuthProvider } from '@/components/ui';
import { useInviteAuth } from '@/contexts/InviteAuthContext';
import { useInviteSocialSignIn } from '@/api/hooks/useInviteSocialSignIn';
import { clearSocialAuthContext, readSocialAuthContext } from '@/utils/socialAuthContext';

export function InviteAuthMethod({
  onSocialSignIn,
  expiresAt,
  onExpire,
}: {
  onSocialSignIn?: (provider: SocialAuthProvider) => void;
  expiresAt?: number;
  onExpire?: () => void;
}) {
  const { token } = useSearch({ from: '/_public/invite' });
  const navigate = useNavigate();
  const social = useInviteSocialSignIn(token);
  const [callbackError] = useState(() => {
    const context = readSocialAuthContext();
    if (context?.kind === 'invite' && context.magicToken === token && context.phase === 'failed') {
      return 'Sign-in was cancelled or could not be completed. Please try again.';
    }
    return '';
  });
  useEffect(() => {
    if (callbackError) {
      clearSocialAuthContext();
    }
  }, [callbackError]);
  const inviteAuth = useInviteAuth();
  const [expiredDeadline, setExpiredDeadline] = useState<number>();
  const deadline = expiresAt ?? inviteAuth?.blockedUntil;
  const isBlocked = deadline !== undefined && deadline !== expiredDeadline;

  const tokenValue = token;
  const hasToken = tokenValue.trim().length > 0;

  return (
    <PageLayout className={isBlocked ? 'pb-8' : 'py-8'} gridProps={{ className: 'px-5' }}>
      <div className={isBlocked ? 'flex flex-col gap-8' : 'flex flex-col gap-6'}>
        {isBlocked ? (
          <AccountLockout
            expiresAt={deadline}
            onExpire={() => {
              setExpiredDeadline(deadline);
              inviteAuth?.clearBlocked();
              onExpire?.();
            }}
          />
        ) : (
          <Button
            variant="primary"
            size="large"
            fullWidth
            disabled={!hasToken || social.pendingProvider !== undefined}
            onClick={() => void navigate({ to: '/invite/email', search: { token: tokenValue } })}
            data-testid="invite-continue-with-email-button"
          >
            Continue with email
          </Button>
        )}

        <div className="flex items-center gap-3">
          <div className="flex-1">
            <Divider />
          </div>
          <Typography variant="body" size="medium" color="bodylighter" as="span">
            {isBlocked ? 'Or continue with' : 'Or'}
          </Typography>
          <div className="flex-1">
            <Divider />
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <SocialAuthButton
            provider="Facebook"
            onClick={() =>
              onSocialSignIn ? onSocialSignIn('Facebook') : void social.signIn('Facebook')
            }
            disabled={!hasToken || social.pendingProvider !== undefined}
            isLoading={social.pendingProvider === 'Facebook'}
            data-testid="invite-facebook-button"
          />
          <SocialAuthButton
            provider="Google"
            onClick={() =>
              onSocialSignIn ? onSocialSignIn('Google') : void social.signIn('Google')
            }
            disabled={!hasToken || social.pendingProvider !== undefined}
            isLoading={social.pendingProvider === 'Google'}
            data-testid="invite-google-button"
          />
          <SocialAuthButton
            provider="Apple"
            onClick={() => (onSocialSignIn ? onSocialSignIn('Apple') : void social.signIn('Apple'))}
            disabled={!hasToken || social.pendingProvider !== undefined}
            isLoading={social.pendingProvider === 'Apple'}
            data-testid="invite-apple-button"
          />
        </div>
        {(social.error || callbackError) && (
          <div role="alert">
            <Typography variant="body" size="medium" color="error" align="center">
              {social.error || callbackError}
            </Typography>
          </div>
        )}
      </div>
    </PageLayout>
  );
}

export default InviteAuthMethod;
